import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';

import { Trip } from '../../classes/trip';
import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import {
  BOTTOM_CENTER_POSITION,
  BOTTOM_LEFT_POSITION,
  OverlayPanel,
} from 'src/app/core/services/overlay-panel';
import { OverlayPanelRef } from 'src/app/core/services/overlay-panel/overlay-panel-ref';

import { DurationTransformerPipe } from './duration-transformer.pipe';
import { TravellersTransformerPipe } from './travellers-transformer.pipe';

import { AuthService } from 'src/app/core/services/auth.service';
import { GoogleAnalyticService } from 'src/app/core/services/google-analytic.service';

import { TravelersPanelComponent } from '../travelers-panel/travelers-panel.component';
import { LocationsPanelComponent } from '../locations-panel/locations-panel.component';
import { DateRangePanelComponent } from '../date-range-panel/date-range-panel.component';

import { TripBase } from '../../classes/trip-base';

import dayjs from 'dayjs';
import moment from 'moment';

@Component({
  selector: 'app-trip-wizard',
  imports: [MatIconModule, DurationTransformerPipe, TravellersTransformerPipe],
  templateUrl: './trip-wizard.component.html',
  styleUrl: './trip-wizard.component.scss',
})
export class TripWizardComponent extends TripBase implements OnInit {
  @Input({ required: true }) public mode: 'standalone' | 'header' =
    'standalone';

  @ViewChild('endInput') readonly endInputRef: ElementRef;
  @ViewChild('dateRef') readonly dateRef: ElementRef;
  @ViewChild('travellersRef') readonly travellersRef: ElementRef;

  public trip: WritableSignal<Trip>;
  protected readonly eSvgIcons = ESvgIcons;

  private locationPanelRef: OverlayPanelRef | null = null;

  #googleAnalyticService = inject(GoogleAnalyticService);
  #overlayPanelService = inject(OverlayPanel);
  #authService = inject(AuthService);
  #router = inject(Router);

  get isLocationPanelOpen(): boolean {
    if (!this.locationPanelRef) return false;
    return this.locationPanelRef.isOpen();
  }

  public displayWith = (value: any) => value?.label ?? value;

  public ngOnInit(): void {
    this.restoreTripData();
  }

  public showTravelersPanel(ref: HTMLElement): void {
    if (this.locationPanelRef && this.locationPanelRef.isOpen()) {
      this.locationPanelRef.close();
    }
    const panelRef = this.#overlayPanelService.open(TravelersPanelComponent, {
      origin: ref,
      position: BOTTOM_CENTER_POSITION,
      data: this.trip().rooms,
    });
    const subs = panelRef.valueChanged().subscribe((rooms) => {
      this.trip.update((trip) => trip.updateTrip({ rooms }));
    });
    panelRef.afterClosed().subscribe(() => subs.unsubscribe());
  }

  public showDurationPanel(ref: HTMLElement): void {
    if (this.locationPanelRef && this.locationPanelRef.isOpen()) {
      this.locationPanelRef.close();
    }
    const { numDays, isSpecifyDates, tripStartDate, tripEndDate } = this.trip();
    const panelRef = this.#overlayPanelService.open(DateRangePanelComponent, {
      origin: ref,
      position: BOTTOM_CENTER_POSITION,
      data: { isSpecifyDates, numDays, tripStartDate, tripEndDate },
    });
    const subs = panelRef
      .valueChanged()
      .subscribe(
        (
          dateData: Pick<
            Trip,
            'numDays' | 'isSpecifyDates' | 'tripStartDate' | 'tripEndDate'
          >,
        ) => {
          this.trip.update((trip) => trip.updateTrip(dateData));

          if (
            dateData.isSpecifyDates &&
            dateData.tripEndDate &&
            !dateData['isAutoEmmit']
          ) {
            panelRef.close();
            const { nativeElement } = this.travellersRef;
            this.showTravelersPanel(nativeElement);
          }
        },
      );
    panelRef.afterClosed().subscribe(() => subs.unsubscribe());
  }

  public showLocationPanel(ref: HTMLElement): void {
    this.locationPanelRef = this.#overlayPanelService.open(
      LocationsPanelComponent,
      {
        origin: ref,
        hasBackdrop: false,
        position: BOTTOM_LEFT_POSITION,
        data: this.trip,
      },
    );
    this.locationPanelRef.valueChanged().subscribe(() => {
      const { nativeElement } = this.dateRef;
      if (nativeElement) {
        this.showDurationPanel(nativeElement);
      }
    });
  }

  public navigateToTripPlanner(origin: HTMLElement, deskWizard = false): void {
    const trip = this.trip();
    const setTripState = () => {
      this.trip.update((trip) =>
        trip.updateTrip({
          isOptimizedMode: true,
          name: '',
          savesId: '',
          blogUrl: '',
          plannerInfo: {
            type: 'classic',
            name: '',
            description: '',
          },
          includes: [],
          excludes: [],
        }),
      );
      this.updateTripState();
    };
    if (this.locationPanelRef && this.locationPanelRef.isOpen()) {
      this.locationPanelRef.close();
    }

    if (trip.isPlanningTripLocation) {
      this.tripWizardStateService.onNewCreateTrip$.next();
      if (!trip.startingLocationId) {
        if (deskWizard) {
          this.showLocationPanel(origin);
        } else {
          this.setFocusToStartInField(origin);
        }
        return;
      }
      if (!trip.endingLocationId) {
        if (deskWizard) {
          this.showLocationPanel(origin);
        } else {
          this.setFocusToStartInField(this.endInputRef.nativeElement);
        }
        return;
      }
      if (!trip.numDays) {
        this.trip.update((trip) =>
          trip.updateTrip({
            numDays: 7,
            tripStartDate: dayjs(new Date().toDateString())
              .add(1, 'day')
              .toDate(),
          }),
        );
      }
      setTripState();
      this.tripWizardStateService.byUserInteraction = true;
      this.#googleAnalyticService.setClassicTripPlanerAnalytic({
        starting_dest_country_ID: trip.route[0]?.countryId || '',
        starting_dest_country_name: trip.route[0]?.name.split(', ')[1] || '',
        starting_dest_city_ID: trip.route[0]?.id || '',
        starting_dest_city_name: trip.route[0]?.name?.split(', ')[0] || '',
        ending_dest_country_ID: trip.route[trip.route.length - 1]?.countryId,
        ending_dest_city_ID: trip.route[trip.route.length - 1]?.id,
        ending_dest_city_name:
          trip.route[trip.route.length - 1]?.name.split(', ')[0] || '',
        ending_dest_country_name:
          trip.route[trip.route.length - 1]?.name.split(', ')[1] || '',
        rooms: trip.rooms.rooms,
        duration_or_specify_dates: trip.isSpecifyDates
          ? 'specify dates'
          : 'duration',
        duration: `${trip.numDays} days`,
        start_date: trip.isSpecifyDates
          ? trip.tripStartDate
            ? moment(trip.tripStartDate).format('DD/MM/YYYY')
            : ''
          : '',
        end_date: trip.isSpecifyDates
          ? trip.tripEndDate
            ? moment(trip.tripEndDate).format('DD/MM/YYYY')
            : ''
          : '',
        pax_total: trip.rooms.adults + trip.rooms.children,
        pax_adults: trip.rooms.adults,
        pax_children: trip.rooms.children,
        userID: this.#authService.userId,
      });
      this.#router.navigate(['trip-planner']).then();
    } else {
      if (!trip.cityInfo) {
        if (deskWizard) {
          this.showLocationPanel(origin);
        } else {
          this.setFocusToStartInField(origin);
        }
        return;
      }
      setTripState();
      const urlArray: string[] = trip.cityInfo.url
        .split('/')
        .filter((url) => !!url);
      this.#router.navigate(urlArray).then();
    }
  }

  private restoreTripData(): void {
    this.trip = signal(this.tripWizardStateService.currentTripStateValue);
    this.startInBoxFocused.set(!!this.trip().startingLocationId);
  }
}
